/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: select.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Select component for dropdown selections with consistent
    styling, validation states, and accessibility features.

 🔧 Notes:
    - Implements select dropdown with consistent styling
    - Supports different validation states and error handling
    - Integrates with Tailwind CSS for responsive design
    - Provides accessibility features and keyboard navigation
    - Supports custom className and all HTML select attributes

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Search } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: (SelectOption | SelectGroup)[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  searchable?: boolean;
}

function Select({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  className = "",
  disabled = false,
  searchable = false
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Helper to check if item is group
  const isGroup = (item: SelectOption | SelectGroup): item is SelectGroup => {
    return 'options' in item;
  };

  // Selected option (memoized)
  const selectedOption = useMemo(() => {
    for (const item of options) {
      if (isGroup(item)) {
        const found = item.options.find(opt => opt.value === value);
        if (found) return found;
      } else if (item.value === value) {
        return item;
      }
    }
    return undefined;
  }, [options, value]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const lowerQuery = searchQuery.toLowerCase();

    return options.reduce<(SelectOption | SelectGroup)[]>((acc, item) => {
      if (isGroup(item)) {
        const filteredGroupOptions = item.options.filter(opt =>
          opt.label.toLowerCase().includes(lowerQuery)
        );
        if (filteredGroupOptions.length > 0) {
          acc.push({ ...item, options: filteredGroupOptions });
        }
      } else {
        if (item.label.toLowerCase().includes(lowerQuery)) {
          acc.push(item);
        }
      }
      return acc;
    }, []);
  }, [options, searchQuery]);

  // Close select when clicking outside (memoized)
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      // Small delay to allow animation to start/render
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, searchable]);

  const handleSelect = useCallback((optionValue: string) => {
    onValueChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  }, [onValueChange]);

  const renderOption = (option: SelectOption) => (
    <button
      key={option.value}
      type="button"
      onClick={() => handleSelect(option.value)}
      className={`
        w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none
        flex items-center justify-between
        ${value === option.value ? 'bg-muted' : ''}
      `}
    >
      <span className="text-foreground truncate">{option.label}</span>
      {value === option.value && (
        <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
      )}
    </button>
  );

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left bg-background border border-input rounded-md
          focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-ring'}
          flex items-center justify-between
        `}
      >
        <span className={`${selectedOption ? 'text-foreground' : 'text-muted-foreground'} truncate`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full min-w-[180px] mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col"
          >
            {searchable && (
              <div className="p-2 border-b border-border sticky top-0 bg-background z-10">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted/50 border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-ring text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            <div className="overflow-y-auto flex-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((item, index) => {
                  if (isGroup(item)) {
                    return (
                      <div key={`group-${index}`}>
                        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/30 sticky top-0 backdrop-blur-sm">
                          {item.label}
                        </div>
                        {item.options.map(opt => renderOption(opt))}
                      </div>
                    );
                  }
                  return renderOption(item);
                })
              ) : (
                <div className="px-3 py-4 text-sm text-center text-muted-foreground">
                  No options found
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default memo(Select);
